import React from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2, icons } from "oolib";
import {
  UI_BODY_BOLD_DF,
  UI_BODY_SM,
  UI_CAPTION,
  UI_CAPTION_BOLD,
  UI_TAG,
  UI_TAG_CONDENSED,
  UI_TITLE,
} from "oolib/dist/v2/components/Typo2";
import TooltipV2 from "@/components/UI/Tooltip";
import { useGetLensOverview, useGetOverviewDetail } from "../../hooks/useGetLensData";
import { useDetailPanel } from "../../comps/ActivePanelContext";
import { TagDetailSidebar } from "../comps/TagDetailSidebar";
import { ConfidenceIndicator } from "../comps/ConfidenceIndicator";
import { TransparencyPanel } from "../comps/TransparencyPanel";
import { SentimentBar } from "../comps/SentimentBar";
import { Sparkline } from "../comps/Sparkline";
import { SIGNAL_COLORS, SENTIMENT_THEME } from "../constants";
import { genIcon } from "oolib/dist/v2/components/Dropdowns/utils/genIcon";
import { ArrowUpRight } from "@phosphor-icons/react";

const { ArrowUp, ArrowDown, Minus, Info } = icons;

const SIGNAL_SUPPORTIVE_COLORS = {
  positive: "#157704",
  negative: "#CF1616",
  neutral: colors2.grey40,
}

const OverviewContainer = styled.div`
  display: flex;
  border-radius: 1.2rem;
  border: 1px solid ${colors2.grey10};
  background: ${colors2.white};
  overflow: hidden;
`;

const Card = styled.div`
  flex: 1;
  gap: 1.4rem;
  min-width: 0;
  padding: 1.4rem 1.6rem;
  border-right: 1px solid ${colors2.grey10};
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &:last-child {
    border-right: none;
  }

  &:hover {
    background: ${colors2.grey5};
  }
`;

const CardGlow = styled.span`
  position: absolute;
  top: -160px;
  left: -40px;
  width: 151px;
  height: 151px;
  border-radius: 145px;
  background: ${({ $color }) => $color};
  filter: blur(60px);
  pointer-events: none;
  z-index: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  position: relative;
  z-index: 1;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const SectionLabel = styled(UI_TAG)`
  color: ${colors2.grey50};
  text-transform: uppercase;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const StatusIconWrap = styled.span`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatusValue = styled(UI_TITLE)`
  color: ${({ $color }) => $color};
`;

const ChangeText = styled(UI_CAPTION)`
  color: ${({ $color }) => $color};
`;

const SecondaryPill = styled.div`
  background: ${colors2.grey5};
  border-radius: 0.9rem;
  padding: 1.2rem;
  min-width: 11rem;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SecondaryPillLabel = styled(UI_TAG_CONDENSED)`
  color: ${colors2.grey40};
  text-transform: uppercase;
`;

const SecondaryPillValue = styled(UI_BODY_BOLD_DF)`
  color: ${colors2.grey60};
`;

const DataRow = styled(UI_CAPTION)`
  color: ${colors2.grey50};
  position: relative;
  z-index: 1;
`;

const SparklineWrap = styled.div`
  width: calc(100% + 32px);
  height: 64px;
  margin-top: -20px;
  margin-bottom: -14px;
  margin-inline: -16px;
`;

const EmptyShell = styled.div`
  border-radius: 1.2rem;
  border: 1px solid ${colors2.grey10};
  background: ${colors2.white};
  padding: 2rem;
`;

const EmptyText = styled(UI_BODY_SM)`
  color: ${colors2.grey40};
`;

const SkeletonRow = styled.div`
  display: flex;
`;

const SkeletonCell = styled.div`
  flex: 1;
  padding: 1.4rem 1.6rem;
  border-right: 1px solid ${colors2.grey10};

  &:last-child {
    border-right: none;
  }
`;

function CalculationTooltip({ basis }) {
  return (
    <div style={{ maxWidth: 320, padding: "10px 12px" }}>
      <UI_BODY_SM style={{ color: colors2.white, fontWeight: 600 }}>{basis.formula}</UI_BODY_SM>
      {(basis.groups || []).map((group) => (
        group.signals?.length > 0 && (
          <div key={group.label} style={{ marginTop: 8 }}>
            <UI_CAPTION style={{ color: "rgba(255,255,255,0.6)", marginBottom: 2 }}>{group.label}</UI_CAPTION>
            <UI_BODY_SM style={{ color: colors2.white, lineHeight: 1.6 }}>
              {group.signals.join(", ")}
            </UI_BODY_SM>
          </div>
        )
      ))}
    </div>
  );
}

function SecondaryMetricPill({ label, value }) {
  return (
    <SecondaryPill>
      <SecondaryPillLabel>{label}</SecondaryPillLabel>
      <SecondaryPillValue>{value}</SecondaryPillValue>
    </SecondaryPill>
  );
}

const OverviewSkeleton = () => (
  <OverviewContainer>
    <SkeletonRow style={{ width: "100%" }}>
      {[1, 2, 3, 4].map((i) => (
        <SkeletonCell key={i}>
          <SkeletonLoader style={{ height: 10, width: 80, borderRadius: 4, marginBottom: 12 }} />
          <SkeletonLoader style={{ height: 18, width: 110, borderRadius: 4, marginBottom: 8 }} />
          <SkeletonLoader style={{ height: 10, width: 130, borderRadius: 4, marginBottom: 14 }} />
          <SkeletonLoader style={{ height: 10, width: "100%", borderRadius: 4, marginBottom: 4 }} />
          <SkeletonLoader style={{ height: 10, width: "90%", borderRadius: 4 }} />
        </SkeletonCell>
      ))}
    </SkeletonRow>
  </OverviewContainer>
);

// ── Sidebar wrapper that fetches detail data ──────────────────────

function OverviewDetailSidebar({ isOpen, onClose, cardId, period, locality }) {
  const { data, isLoading } = useGetOverviewDetail({ cardId, period, locality, enabled: isOpen && !!cardId });

  // Map metricBreakdown to mentions format for TagDetailSidebar
  const mentions = (data?.annotations || []).map((a) => ({
    text: a.text,
    tone: a.sentiment || "neutral",
  }));

  return (
    <TagDetailSidebar
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      width={420}
      title={data?.label}
      overline=""
      mentionsCount={data?.totalMentions}
      description={data?.description}
      sources={data?.sources}
      trendData={data?.trend}
      mentions={mentions}
      showSentimentPolarity={cardId === "user_sentiment"}
      primaryColor={colors2.grey80}
      glowColor="rgba(0,0,0,0.04)"
    />
  );
}

// ── Main component ────────────────────────────────────────────────

export function OverviewBlock({ period, locality, showTransparency }) {
  const { data, isLoading } = useGetLensOverview({ period, locality });
  const { selected, select, deselect, isOpen } = useDetailPanel("lenses:overview");

  if (isLoading) return <OverviewSkeleton />;
  if (!data?.cards?.length) {
    return (
      <EmptyShell>
        <EmptyText>No data available for this period.</EmptyText>
      </EmptyShell>
    );
  }

  return (
    <>
      <OverviewContainer>
        {data.cards.map((card) => {
          const state = card.state || (card.signal === "negative" ? "bad" : "good");
          const trendDir = card.trendDir || "stable";

          let stateColor;
          let stateSupportColor;

          if (trendDir === "declining") {
            stateColor = SIGNAL_COLORS.negative;
            stateSupportColor = SIGNAL_SUPPORTIVE_COLORS.negative;
          } else if (trendDir === "improving" && state === "bad") {
            stateColor = SIGNAL_COLORS.neutral;
            stateSupportColor = SIGNAL_SUPPORTIVE_COLORS.neutral;
          } else if (state === "bad") {
            stateColor = SIGNAL_COLORS.negative;
            stateSupportColor = SIGNAL_SUPPORTIVE_COLORS.negative;
          } else {
            stateColor = SIGNAL_COLORS.positive;
            stateSupportColor = SIGNAL_SUPPORTIVE_COLORS.positive;
          }
          const TrendIcon = trendDir === "improving" ? ArrowUp
            : trendDir === "declining" ? ArrowDown
            : Minus;
          const signal = card.signal || "neutral";
          const changeStr = card.change || "";
          const stateLabel = card.value || "Stable";

          const secondaryMetric = card.topEmotion && card.topEmotion !== "None"
            ? { label: "Top Emotion", value: card.topEmotion }
            : card.mentions != null
              ? { label: card.mentionsLabel || "Mentions", value: card.mentions }
              : null;

          const changeIsAbsolute =
            changeStr === "No previous data" ||
            changeStr === "No change" ||
            changeStr.includes("last period") ||
            changeStr.includes("last month");

          // Distribution as plain text
          const dist = card.distribution;
          const distText = dist && dist.total > 0
            ? [
                dist.positivePercent > 0 && `${dist.positivePercent}% Positive`,
                dist.neutralPercent > 0 && `${dist.neutralPercent}% Neutral`,
                dist.negativePercent > 0 && `${dist.negativePercent}% Negative`,
              ].filter(Boolean).join("  ·  ")
            : null;

          // Sparkline as plain text
          const spark = card.sparkline;
          const sparkGranularity = spark?.[0]?.granularity || "weekly";
          const sparkText = spark?.length > 1
            ? spark.map((s) => `${s.label || s.week || s.index}: ${s.count}`).join("  ·  ")
            : null;

          return (
            <Card key={card.id} onClick={() => select(card.id)}>
              <CardGlow $color={(SENTIMENT_THEME[signal] || SENTIMENT_THEME.neutral).glow} />

              <HeaderRow>
                <HeaderLeft>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <SectionLabel>{card.label}</SectionLabel>
                    {card.calculationBasis && (
                      <TooltipV2
                        primaryContent={{
                          layout: "custom",
                          render: (
                            <CalculationTooltip basis={card.calculationBasis} />
                          ),
                        }}
                      >
                        <Info size={16} color={colors2.grey30} style={{ cursor: "pointer" }} />
                      </TooltipV2>
                    )}
                  </div>

                  <StatusRow>
                    <StatusIconWrap $color={stateColor}>
                      <TrendIcon size={16} weight="bold" color={colors2.white} />
                    </StatusIconWrap>
                    <StatusValue $color={stateColor}>{stateLabel}</StatusValue>
                  </StatusRow>

                  <ChangeText $color={stateSupportColor}>
                    {changeIsAbsolute ? changeStr : `${changeStr} from last month`}
                  </ChangeText>
                </HeaderLeft>

                {/* {secondaryMetric && (
                  <SecondaryMetricPill
                    label={secondaryMetric.label}
                    value={secondaryMetric.value}
                  />
                )} */}
                <ArrowUpRight size={14} weight="bold" color={colors2.grey40} />
              </HeaderRow>

              {/* {distText && <DataRow>{distText}</DataRow>} */}
              {dist && (
                <SentimentBar
                  showLegend
                  segments={[
                    { key: "positive", value: dist.positivePercent, color: SENTIMENT_THEME.positive.bg },
                    { key: "neutral", value: dist.neutralPercent, color: SENTIMENT_THEME.neutral.bg },
                    { key: "negative", value: dist.negativePercent, color: SENTIMENT_THEME.negative.bg },
                  ]}
                />
              )}

              {/* {sparkText && <DataRow>{sparkGranularity}: {sparkText}</DataRow>} */}

              {!dist && spark?.length > 1 && (
                <SparklineWrap>
                  <Sparkline data={spark} color={stateColor} />
                </SparklineWrap>
              )}
            </Card>
          );
        })}
      </OverviewContainer>

      <OverviewDetailSidebar
        isOpen={isOpen}
        onClose={deselect}
        cardId={selected}
        period={period}
        locality={locality}
      />
    </>
  );
}
